import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Layout } from 'react-grid-layout'
import { useSelector } from 'react-redux'
import { contextCompanyIdSelector } from '../../../../redux/context/company/selectors'
import { CustomReport } from '../../../../redux/context/customReports/types'
import { CustomReportSectionsType } from '../../../../redux/context/customReports/typesSection'
import SectionModal from './section/CustomReportSectionModal'
import { StyledResponsiveGridLayout } from './layout/StyledComponents'
import LayoutItem from './layout/LayoutItem'
import Loading from '../../../../shared/components/Loading'
import { BREAKPOINTS, COLS, ROW_HEIGHT, ResponsiveLayouts, getDataGrid } from './grid.utils'
import { LayoutsContext, LayoutsContextType } from './layout/LayoutContext'
import SectionDataLoader from './section/SectionDataLoader'

const RESIZE_HANDLES_EDIT: string[] = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']
const RESIZE_HANDLES_EMPTY: string[] = []

interface CustomReportGridProps {
  calculatedReport?: CustomReport
}

const CustomReportGrid: React.FC<CustomReportGridProps> = ({ calculatedReport }) => {
  const companyId = useSelector(contextCompanyIdSelector)!
  const [sectionModalVisible, setSectionModalVisible] = useState(false)
  const [editSectionData, setEditSectionData] = useState<CustomReportSectionsType | null>(null)
  const ref = useRef(null)
  const [initialLoading, setLoading] = useState(true)
  const { layouts, layoutEdit, editedLayouts, setEditedLayouts, currentBreakPoint, setCurrentBreakPoint } = useContext(
    LayoutsContext
  ) as LayoutsContextType

  const activeLayouts = layoutEdit ? editedLayouts ?? layouts : layouts

  const onLayoutChange = useCallback(
    (_currentLayout: Layout[], allLayouts: ResponsiveLayouts) => {
      if (!layoutEdit) return
      const edited = Object.fromEntries(
        Object.entries(allLayouts).filter(([, value]) => value.length > 0)
      ) as ResponsiveLayouts
      setEditedLayouts(edited)
    },
    [layoutEdit, setEditedLayouts]
  )

  const loadingHandler = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 1000)
  }

  const editModal = useCallback((editSection: CustomReportSectionsType | null) => {
    setEditSectionData(editSection)
    setSectionModalVisible(true)
  }, [])

  useEffect(() => {
    loadingHandler()
  }, [companyId])

  const children = useMemo(
    () =>
      calculatedReport?.sections?.map(section => {
        const dataGrid = getDataGrid(section, currentBreakPoint, activeLayouts)
        return (
          <LayoutItem
            ref={ref}
            key={section.id}
            data-grid={{
              ...dataGrid,
              static: false,
              isDraggable: layoutEdit,
              isResizable: layoutEdit
            }}
            section={section}
            showDragHandle={layoutEdit}
            content={<SectionDataLoader key={section.id} section={section} editModal={editModal} />}
          />
        )
      }),
    [calculatedReport?.sections, currentBreakPoint, activeLayouts, layoutEdit, editModal]
  )

  if (initialLoading) return <Loading />

  return (
    <>
      {sectionModalVisible && (
        <SectionModal
          visible={sectionModalVisible}
          setVisible={setSectionModalVisible}
          key="add-section"
          reportId={calculatedReport?.id}
          section={editSectionData || null}
        />
      )}

      <StyledResponsiveGridLayout
        layouts={layoutEdit ? editedLayouts ?? layouts : undefined}
        onLayoutChange={layoutEdit ? onLayoutChange : undefined}
        onBreakpointChange={setCurrentBreakPoint}
        draggableHandle={layoutEdit ? '.layout-drag-handle' : undefined}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        resizeHandles={layoutEdit ? RESIZE_HANDLES_EDIT : RESIZE_HANDLES_EMPTY}
      >
        {children}
      </StyledResponsiveGridLayout>
    </>
  )
}

export default CustomReportGrid
